import { Injectable } from '@angular/core';

import {
    CancellationTerms, CartItem, Order, Product, ShoppingCartInput, ShoppingCartProcessor,
    ShortEvent, Tax, Transaction, TransactionItem, TransactionStatus
} from '@azavista/payments';
import { ShoppingCartProcessorUI, ShoppingCartResultUI } from '@azavista/payments/lib/shopping-cart/processor-ui';

import { ICartItem, IShoppingCartV2Data, ISimpleEvent } from './interfaces';
import { ICartItemProduct, IOrder, IOrderItem, IOrderProduct, IOrderTransaction, IPaymentProduct, IPaymentProductCancellationTermItem, IPaymentTax } from '../shared/interfaces';
import { IProductVisibility, IProductWithQuantity } from '../shared/interfaces';
import { AppLoggerService, LogLevel } from '../shared/app-logger.service';

@Injectable({
    providedIn: 'root'
})
export class ShoppingCartV2Service {
    public processor: ShoppingCartProcessor | null;
    private data: IShoppingCartV2Data | null;

    constructor (
        private appLoggerSvc: AppLoggerService,
    ) {}

    init(data: IShoppingCartV2Data | null): void {
        this.data = data;
        if (!data) {
            this.processor = null;
            return;
        }
        const shortEvent = this.convertToShortEvent(data.event);
        const taxes = this.convertTaxes(data.productRespone.taxes);
        const cancellationTerms = this.convertCancellationTerms(data.productRespone.cancellation_terms);
        const products = this.convertProducts(data.productRespone.products, []);
        const orders = this.convertOrders(data.orders);
        this.logGroup(
            'new ShoppingCartProcessor(shortEvent, taxes, cancellationTerms, products, orders)',
            'shortEvent', shortEvent,
            'taxes', taxes,
            'cancellationTerms', cancellationTerms,
            'products', products,
            'orders', orders
        );

        this.processor = new ShoppingCartProcessor(shortEvent, taxes, cancellationTerms, products, orders);
    }

    computeCartItems(): ICartItemProduct[] {
        const computedCartItems = this.processor.computeProducts();
        this.logGroup('result of processor.computeProducts()', computedCartItems);
        return computedCartItems as ICartItemProduct[];
    }

    validateItems(cartItems: CartItem[]): boolean {
        return this.processor.validateItems(cartItems);
    }

    process(input: ShoppingCartInput): ShoppingCartResultUI {
        if (!this.processor) {
            return {
                condensedItems: [],
                currentOrder: null,
                errors: [],
                order: null,
                totals: {
                    cancellation_cost: 0,
                    current_order_total: 0,
                    other: 0,
                    tax: 0,
                    paid:0,
                    total: 0,
                    total_discount: 0
                },
                donationProducts: [],
            };
        }
        const uiProcessor = new ShoppingCartProcessorUI(this.processor);
        this.logGroup('ShoppingCartProcessorUI.process(input)', 'input', input);
        const resultUI = uiProcessor.process(input);
        this.logGroup('result of ShoppingCartProcessorUI.process(input)', resultUI);
        return resultUI;
    }

    getCartItems(cartItems: ICartItemProduct[], productsVisibility: IProductVisibility[]): ICartItem[] {
        const result: ICartItem[] = cartItems.map(x => this.convertToCartItem(x.product as IPaymentProduct, productsVisibility));
        return result;
    }

    getProcessorCartItems(cartItems: ICartItem[]): CartItem[] {
        const result = cartItems.map(x => {
            const ci: CartItem = { product_id: x.product.id, quantity: x.selectedQuantity };
            return ci;
        });
        return result;
    }

    convertToCartItem(product: IPaymentProduct, productsVisibility: IProductVisibility[]): ICartItem {
        const maxQuantity = product.available_quantity != null ? (
                product.max_per_buyer != null ?
                    Math.min(product.available_quantity, product.max_per_buyer) : product.available_quantity
            ) : (
                product.max_per_buyer != null ? product.max_per_buyer : undefined
            );
        const cartItem: ICartItem = {
            product: product,
            hidden: this.isProductHidden(productsVisibility, product.id),
            minQuantity: product.min_per_buyer || 0,
            quantityConstrained: maxQuantity != undefined,
            maxQuantity: maxQuantity ?? 0,
            selectedQuantity: 0,
            soldOut: this.isPaymentProductSoldOut(product),
        };
        return cartItem;
    }

    isPaymentProductSoldOut(product: IPaymentProduct): boolean {
        return product.min_per_buyer === 0 && product.max_per_buyer === 0;
    }

    createEmptyUIResult(): ShoppingCartResultUI {
        const result: ShoppingCartResultUI = {
            condensedItems: [],
            currentOrder: {
                cancellationCost: 0,
                orderItems: [],
                total: 0,
                tax: 0
            },
            errors: [],
            order: {
                cancellationCost: 0,
                orderItems: [],
                total: 0,
                tax: 0
            },
            totals: {
                cancellation_cost: 0,
                current_order_total: 0,
                other: 0,
                tax: 0,
                total: 0,
                paid: 0,
                total_discount: 0
            },
            donationProducts: [],
        };
        return result;
    }

    getVisibleProducts(products: IPaymentProduct[], productsVisibility: IProductVisibility[]): IPaymentProduct[] {
        if (!products || !productsVisibility) {
            return [];
        }
        const visibleProducts: IPaymentProduct[] = [];
        for (const product of products) {
            const isHidden = this.isProductHidden(productsVisibility, product.id);
            if (!isHidden) {
                visibleProducts.push(product);
            }
        }
        return visibleProducts;
    }

    isProductHidden(productsVisibility: IProductVisibility[], productId: string): boolean {
        if (!productsVisibility) {
            return true;
        }
        const item = productsVisibility.find(x => x.productId === productId);
        if (!item) {
            return false;
        }
        return item.hidden;
    }

    createProductsWithQuantityFromCartItems(cartItems: ICartItem[]): IProductWithQuantity[] {
        return cartItems.map(x => ({
            product: x.product,
            quantity: x.selectedQuantity
        } as IProductWithQuantity));
    }

    private logGroup(message?: any, ...optionalParams: any[]): void {
        this.appLoggerSvc.group('@azavista/payments');
        this.appLoggerSvc.log(LogLevel.log, message, ...optionalParams);
        this.appLoggerSvc.groupEnd();
    }

    private convertTaxes(paymentTaxes: IPaymentTax[]): Tax[] {
        const result: Tax[] = [...paymentTaxes];
        return result;
    }

    private convertCancellationTerms(cancellationTerms: IPaymentProductCancellationTermItem[]): CancellationTerms[] {
        const result: CancellationTerms[] = [...cancellationTerms];
        return result;
    }

    private convertProducts(products: IPaymentProduct[], productsVisibility: IProductVisibility[]): Product[] {
        const result: Product[] = [];
        for (const product of products) {
            result.push({
                ...product,
                visible: !this.isProductHidden(productsVisibility, product.id)
            });
        }
        return result;
    }

    private convertOrders(orders: IOrder[]): Order[] {
        if (!orders) {
            return [];
        }
        const result: Order[] = [];
        for (const order of orders) {
            result.push({
                created_at: order.created_at ? new Date(order.created_at) : new Date(),
                discount: order.discount,
                id: order.id,
                status: order.payment_status,
                subtotal: order.subtotal,
                // TODO: How to provide "tax" as number ? The server does not return it
                tax: 0,
                total: order.total,
                transactions: this.convertTransactions(order.transactions)
            });
        }
        return result;
    }

    private convertTransactions(transactions: IOrderTransaction[]): Transaction[] {
        if (!transactions) {
            return [];
        }
        const result: Transaction[] = [];
        for (const trans of transactions) {
            result.push({
                created_at: new Date(trans.created_at),
                discount: trans.discount,
                id: trans.id,
                status: trans.status as TransactionStatus,
                subtotal: trans.subtotal,
                total: trans.total,
                items: this.convertTransactionItems(trans.items),
            });
        }
        return result;
    }

    private convertTransactionItems(items: IOrderItem[]): TransactionItem[] {
        if (!items) {
            return [];
        }
        const result: TransactionItem[] = [];
        for (const item of items) {
            result.push({
                ...item,
                id: item.product.id,
                product: this.convertOrderProduct(item.product, []),
            });
        }
        return result;
    }

    private convertOrderProduct(product: IOrderProduct, productsVisibility: IProductVisibility[]): Product {
        const result: Product = {
            cancellation_terms_id: product.cancellation_terms_id,
            category: product.category,
            currency: product.currency,
            id: product.id,
            min_per_buyer: product.max_per_buyer,
            max_per_buyer: product.max_per_buyer,
            more_than_one: product.more_than_one,
            name: product.name,
            price: product.price,
            tax_id: product.tax_id,
            type: product.type,
            // TODO: The server does not return "dependency_ids" for a product
            dependency_ids: [],
            visible: !this.isProductHidden(productsVisibility, product.id)
        };
        return result;
    }

    // private convertDependencies(dependencies: IDependency[]): Dependency[] {
    //     const result: Dependency[] = dependencies.map(x => {
    //         const dep: Dependency = {
    //             id: x.id,
    //             name: x.name,
    //             criteria: x.criteria.map(c => ({ field: c.field, operator: c.operator, value: c.value } as Record<string, unknown>))
    //         };
    //         return dep;
    //     });
    //     return result;
    // }

    private convertToShortEvent(event: ISimpleEvent): ShortEvent {
        const shortEvent: ShortEvent = {
            id: event.id,
            start: new Date(event.startDate),
            end: new Date(event.endDate),
            name: event.name
        };
        return shortEvent;
    }
}
